/**
 * NEXORA AI - Goals Step (Premium Refactor)
 * Epic 1.3 Phase 2.2 - Onboarding Premium
 * 
 * Émotion cible: PROJECTION
 * Standards:
 * - Cards enrichies: Icône + Titre + Description + Bénéfice concret
 * - Animation sélection: Bounce + Checkmark (Framer Motion)
 * - Message de valorisation après sélection
 * - Touch targets: Cards ≥ 44x44px
 * - Feedback immédiat: Count sélectionnés
 * - Grid responsive: 1 col mobile, 2 cols tablet/desktop
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Target, ArrowRight, ArrowLeft, Check, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const GOALS = [
  { 
    id: 'brand', 
    label: 'goal_brand', 
    desc: 'goal_brand_desc',
    benefit: 'goal_brand_benefit',
    icon: '🎯',
    gradient: 'from-blue-500/10 to-cyan-500/10',
    borderColor: 'border-blue-500/50'
  },
  { 
    id: 'sales', 
    label: 'goal_sales', 
    desc: 'goal_sales_desc',
    benefit: 'goal_sales_benefit',
    icon: '💰',
    gradient: 'from-green-500/10 to-emerald-500/10',
    borderColor: 'border-green-500/50'
  },
  { 
    id: 'followers', 
    label: 'goal_followers', 
    desc: 'goal_followers_desc',
    benefit: 'goal_followers_benefit',
    icon: '👥',
    gradient: 'from-purple-500/10 to-pink-500/10',
    borderColor: 'border-purple-500/50'
  },
  { 
    id: 'automation', 
    label: 'goal_automation', 
    desc: 'goal_automation_desc',
    benefit: 'goal_automation_benefit',
    icon: '⚡',
    gradient: 'from-yellow-500/10 to-orange-500/10',
    borderColor: 'border-yellow-500/50'
  },
  { 
    id: 'content', 
    label: 'goal_content', 
    desc: 'goal_content_desc',
    benefit: 'goal_content_benefit',
    icon: '🤖',
    gradient: 'from-indigo-500/10 to-blue-500/10',
    borderColor: 'border-indigo-500/50'
  },
  { 
    id: 'other', 
    label: 'goal_other', 
    desc: 'goal_other_desc',
    benefit: 'goal_other_benefit',
    icon: '✨',
    gradient: 'from-pink-500/10 to-rose-500/10',
    borderColor: 'border-pink-500/50'
  }
];

export default function GoalsStep({ data, onNext, onBack, onData, currentStep, totalSteps }) {
  const { t } = useTranslation('onboarding');
  const [selected, setSelected] = useState(data.goals || []);
  const [showValidation, setShowValidation] = useState(false);

  const toggleGoal = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(g => g !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const handleNext = () => {
    if (selected.length > 0) {
      onData({ goals: selected });
      setShowValidation(true);
      
      // Delay to show validation message
      setTimeout(() => {
        onNext();
      }, 1200);
    }
  };

  // Animation for cards entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-4xl"
      >
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-500/30 mb-6"
          >
            <Target className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" strokeWidth={1.5} />
          </motion.div>

          <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-3 sm:mb-4">
            {t('goals_title')}
          </h2>
          <p className="text-base md:text-lg text-zinc-400 mb-2 max-w-2xl mx-auto">
            {t('goals_subtitle')}
          </p>

          {/* Helper + Selection count */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm text-zinc-500 mt-4"
          >
            <span>{t('goals_helper')}</span>
            <AnimatePresence mode="wait">
              {selected.length > 0 && (
                <motion.span
                  key={selected.length}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 font-medium"
                >
                  <Check className="w-4 h-4" />
                  {selected.length === 1 
                    ? t('goals_selected', { count: selected.length })
                    : t('goals_selected_plural', { count: selected.length })
                  }
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Goals Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8"
        >
          {GOALS.map((goal) => {
            const isSelected = selected.includes(goal.id);
            
            return (
              <motion.div
                key={goal.id}
                variants={cardVariants}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  onClick={() => toggleGoal(goal.id)}
                  className={`
                    relative p-5 sm:p-6 cursor-pointer transition-all duration-200
                    min-h-[88px] sm:min-h-[120px]
                    ${isSelected 
                      ? `border-2 ${goal.borderColor} bg-gradient-to-br ${goal.gradient}` 
                      : 'border border-zinc-800 bg-zinc-900/30 hover:border-zinc-700 hover:bg-zinc-900/50'
                    }
                  `}
                  data-testid={`goal-${goal.id}`}
                >
                  {/* Checkmark - Animated appearance */}
                  <AnimatePresence>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0, rotate: 180 }}
                        transition={{ type: 'spring', duration: 0.4 }}
                        className="absolute top-3 right-3 w-7 h-7 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg"
                      >
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Icon */}
                    <motion.div
                      animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 0.3 }}
                      className="text-3xl sm:text-4xl flex-shrink-0"
                    >
                      {goal.icon}
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-8">
                      <h3 className="font-semibold text-white mb-1 text-sm sm:text-base">
                        {t(goal.label)}
                      </h3>
                      <p className="text-xs sm:text-sm text-zinc-400 mb-2">
                        {t(goal.desc)}
                      </p>
                      
                      {/* Benefit - Only shown when selected */}
                      <AnimatePresence>
                        {isSelected && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="flex items-center gap-1.5 text-xs text-blue-400 mt-2"
                          >
                            <Sparkles className="w-3 h-3 flex-shrink-0" />
                            <span className="font-medium">{t(goal.benefit)}</span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Validation Message */}
        <AnimatePresence>
          {showValidation && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.3 }}
              >
                <Check className="w-5 h-5 text-green-400" />
              </motion.div>
              <p className="text-sm text-green-400 font-medium">
                {t('validation_goals')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={onBack}
            className="min-h-[44px] text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="goals-back-button"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>

          <Button
            variant="default"
            onClick={handleNext}
            disabled={selected.length === 0 || showValidation}
            className="min-h-[44px] bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg shadow-purple-600/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="goals-continue-button"
          >
            {t('continue')}
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
